import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Tabs,
  Tab,
  TablePagination,
  CircularProgress,
  LinearProgress,
  Divider,
  Alert,
  Tooltip,
  Checkbox,
  MenuItem,
} from "@mui/material";
import {
  Add,
  Close,
  Receipt,
  Download,
  Send,
  Delete,
  Edit,
  KeyboardArrowDown,
  KeyboardArrowUp,
  DoneAll,
} from "@mui/icons-material";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useSnackbar } from "notistack";
import dayjs from "dayjs";

import invoiceService from "../../services/invoiceService";
import InvoiceDialog from "../../components/common/InvoiceDialog";
import EditInvoiceDialog from "../../components/common/EditInvoiceDialog";
import MarkInvoicePaidDialog from "../../components/common/MarkInvoicePaidDialog";
import StatusChip from "../../components/common/StatusChip";
import PageHeader from "../../components/common/PageHeader";
import EmptyState from "../../components/common/EmptyState";
import KpiCard from "../../components/common/KpiCard";
import SkeletonLoader from "../../components/common/SkeletonLoader";
import { selectStatusOptions, selectKeyForRole } from "../../redux/slices/statusDefinitionsSlice";
import { selectUsdToCiRate } from "../../redux/slices/systemSettingsSlice";
import { formatCiWithUsd, ciToUsd } from "../../utils/currency";
import useUrlState from "../../hooks/useUrlState";
import useScrollRestoration from "../../hooks/useScrollRestoration";
import { slugifyTab } from "../../utils/tabSlug";

const URL_SCHEMA = {
  // The tab's slugified label ("all", "paid", …) rather than its array
  // index — stable if tabs are ever reordered, and readable in the address
  // bar/shared links instead of a meaningless number.
  tab: { default: "" },
  page: { default: 0, parse: (v) => parseInt(v, 10) || 0 },
};

const AdminInvoices = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const statusOptions = useSelector(selectStatusOptions("invoice"));
  const paidKey = useSelector(selectKeyForRole("invoice", "paid"));
  const usdToCiRate = useSelector(selectUsdToCiRate);
  const TABS = [{ label: "All", value: "" }, ...statusOptions];

  useScrollRestoration();
  const [{ tab, page }, setUrlState] = useUrlState(URL_SCHEMA);
  const activeTab = Math.max(0, TABS.findIndex((t) => slugifyTab(t.label) === tab));
  const [invoices, setInvoices] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [editInvoice, setEditInvoice] = useState(null);
  const [editing, setEditing] = useState(false);
  const [invoiceToDelete, setInvoiceToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [resendingId, setResendingId] = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  // Customer "Mark as Paid" submissions awaiting confirmation — fetched
  // separately from the tab-filtered `invoices` list below, since a
  // pending submission can sit on an invoice of ANY status and needs to
  // stay visible regardless of which status tab is currently selected.
  const [pendingReviews, setPendingReviews] = useState([]);
  const [reviewingId, setReviewingId] = useState(null);

  // Bulk "Mark as Paid" — admin selects several invoices at once and pays
  // off each one's full remaining balance directly, no customer-submitted
  // proof required (unlike the pending-review flow above). Selection is a
  // Set of invoice _ids, kept across pagination/tab changes until an
  // explicit clear so an admin can build up a selection spanning pages.
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false);
  const [bulkMethod, setBulkMethod] = useState("bank_transfer");
  const [bulkNotes, setBulkNotes] = useState("");
  const [bulkPaying, setBulkPaying] = useState(false);

  // Single-invoice "Mark as Paid" — Paid in Full or Multiple Payments
  // (each with its own method/transaction ID), see MarkInvoicePaidDialog.
  const [markPaidInvoice, setMarkPaidInvoice] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [invRes, sumRes, allRes] = await Promise.all([
        invoiceService.getAll({ status: TABS[activeTab].value || undefined }),
        invoiceService.getSummary(),
        // Deliberately a high limit, not the default 20 — this scan needs
        // to catch a pending review regardless of how far back it is,
        // since it isn't scoped by the status tab.
        invoiceService.getAll({ limit: 500 }),
      ]);
      setInvoices(invRes.data.data || []);
      setSummary(sumRes.data.data || null);
      setPendingReviews((allRes.data.data || []).filter((inv) => inv.pendingPayment?.submittedAt));
    } catch {
      enqueueSnackbar("Failed to load invoices", { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  const toggleSelected = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // "Select all" toggles just the current page's SELECTABLE (unpaid) rows
  // — matches the checkboxes actually visible/clickable on screen right
  // now, rather than silently reaching into other pages.
  const currentPageInvoices = invoices.slice(page * 10, page * 10 + 10);
  const selectablePageInvoices = currentPageInvoices.filter((inv) => inv.balanceDue > 0);
  const allPageSelected =
    selectablePageInvoices.length > 0 && selectablePageInvoices.every((inv) => selectedIds.has(inv._id));
  const somePageSelected = selectablePageInvoices.some((inv) => selectedIds.has(inv._id));

  const toggleSelectAllOnPage = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allPageSelected) {
        selectablePageInvoices.forEach((inv) => next.delete(inv._id));
      } else {
        selectablePageInvoices.forEach((inv) => next.add(inv._id));
      }
      return next;
    });
  };

  const handleBulkMarkPaid = async () => {
    setBulkPaying(true);
    try {
      const { data } = await invoiceService.bulkMarkPaid([...selectedIds], {
        method: bulkMethod,
        notes: bulkNotes || undefined,
      });
      enqueueSnackbar(data.message || "Invoices marked as paid", { variant: "success" });
      setSelectedIds(new Set());
      setBulkConfirmOpen(false);
      setBulkNotes("");
      await load();
    } catch (err) {
      enqueueSnackbar(err.response?.data?.message || "Failed to mark invoices as paid", { variant: "error" });
    } finally {
      setBulkPaying(false);
    }
  };

  const handleViewProof = async (invoiceId) => {
    try {
      const { data } = await invoiceService.getPaymentProofUrl(invoiceId);
      window.open(data.data.url, "_blank");
    } catch (err) {
      enqueueSnackbar(err.response?.data?.message || "Failed to load payment proof", { variant: "error" });
    }
  };

  const handleApproveReview = async (invoiceId) => {
    setReviewingId(invoiceId);
    try {
      await invoiceService.approvePayment(invoiceId);
      enqueueSnackbar("Payment approved", { variant: "success" });
      await load();
    } catch (err) {
      enqueueSnackbar(err.response?.data?.message || "Failed to approve payment", { variant: "error" });
    } finally {
      setReviewingId(null);
    }
  };

  const handleRejectReview = async (invoiceId) => {
    const reason = window.prompt("Reason (optional) — shown to the customer:") || "";
    setReviewingId(invoiceId);
    try {
      await invoiceService.rejectPayment(invoiceId, reason);
      enqueueSnackbar("Payment submission rejected", { variant: "info" });
      await load();
    } catch (err) {
      enqueueSnackbar(err.response?.data?.message || "Failed to reject payment", { variant: "error" });
    } finally {
      setReviewingId(null);
    }
  };

  useEffect(() => {
    load();
  }, [activeTab]);

  const handleCreate = async (form) => {
    setCreating(true);
    try {
      await invoiceService.create({
        customerEmail: form.customerEmail,
        customer: form.customer,
        shipment: form.shipment,
        lines: form.lines,
        dueDate: form.dueDate,
        notes: form.notes,
      });
      enqueueSnackbar("Invoice created and sent to customer", {
        variant: "success",
      });
      setCreateOpen(false);
      load();
    } catch (err) {
      enqueueSnackbar(
        err.response?.data?.message || "Failed to create invoice",
        { variant: "error" },
      );
    } finally {
      setCreating(false);
    }
  };

  const handleEditSave = async (form) => {
    setEditing(true);
    try {
      await invoiceService.update(editInvoice._id, form);
      enqueueSnackbar("Invoice updated", { variant: "success" });
      setEditInvoice(null);
      load();
    } catch (err) {
      enqueueSnackbar(err.response?.data?.message || "Failed to update invoice", { variant: "error" });
    } finally {
      setEditing(false);
    }
  };

  const handleResend = async (invoice) => {
    setResendingId(invoice._id);
    try {
      const { data } = await invoiceService.resend(invoice._id);
      enqueueSnackbar(data.message || "Invoice resent", { variant: "success" });
    } catch (err) {
      enqueueSnackbar(err.response?.data?.message || "Failed to resend invoice", { variant: "error" });
    } finally {
      setResendingId(null);
    }
  };

  const handleDelete = async () => {
    if (!invoiceToDelete) return;
    setDeleting(true);
    try {
      const { data } = await invoiceService.delete(invoiceToDelete._id);
      enqueueSnackbar(data.message || "Invoice deleted", { variant: "success" });
      setInvoiceToDelete(null);
      load();
    } catch (err) {
      enqueueSnackbar(err.response?.data?.message || "Failed to delete invoice", { variant: "error" });
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <PageHeader
        title="Invoices"
        subtitle={`${invoices.length} invoices`}
        action={
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button variant="outlined" size="small" startIcon={<Download />}>
              Export
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={() => setCreateOpen(true)}
            >
              Manual Invoice
            </Button>
            <Button
              variant="contained"
              size="small"
              startIcon={<Add />}
              onClick={() => navigate("/admin/generate-invoice")}
            >
              Generate Invoice
            </Button>
          </Box>
        }
      />

      <Box
        sx={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 2 }}
      >
        {/* All four tiles come straight from getFinanceSummary now — that
            endpoint already aggregates across EVERY invoice (not just
            whatever page happens to be loaded in the table below) and
            already excludes House Accounts. Total Outstanding/Paid used to
            be computed from the local, paginated `invoices` array instead,
            which silently only reflected the most-recently-loaded ~20
            invoices and never excluded House Accounts. */}
        <KpiCard
          label="Total Expected"
          value={formatCiWithUsd(summary?.totalExpected || 0, usdToCiRate)}
          delta="Sum of all invoice amounts"
          deltaType="neutral"
          icon={<Receipt />}
          iconBg="#EBF4FF"
          iconColor="#2B6CB0"
        />
        <KpiCard
          label="Total Outstanding"
          value={formatCiWithUsd(summary?.outstanding || 0, usdToCiRate)}
          delta="Sum of balance due"
          deltaType="down"
          icon={<Receipt />}
          iconBg="#FFF5F5"
          iconColor="#C53030"
        />
        <KpiCard
          label="Unpaid Invoices"
          value={(summary?.outstandingCount || 0).toString()}
          delta="Invoice count"
          deltaType="down"
          icon={<Receipt />}
          iconBg="#FFFBEB"
          iconColor="#C05621"
        />
        <KpiCard
          label="Paid Invoices"
          value={(summary?.paidCount || 0).toString()}
          delta="Invoice count"
          deltaType="up"
          icon={<Receipt />}
          iconBg="#ECFDF5"
          iconColor="#0E9F6E"
        />
      </Box>

      {pendingReviews.length > 0 && (
        <Card sx={{ borderLeft: "4px solid", borderColor: "warning.main" }}>
          <CardContent>
            <Typography fontWeight={700} fontSize={14} sx={{ mb: 1.5 }}>
              Payment Submissions Awaiting Review ({pendingReviews.length})
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Invoice</TableCell>
                  <TableCell>Customer</TableCell>
                  <TableCell align="right">Amount</TableCell>
                  <TableCell>Method</TableCell>
                  <TableCell>Submitted</TableCell>
                  <TableCell>Proof</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pendingReviews.map((inv) => (
                  <TableRow key={inv._id} hover>
                    <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }}>{inv.invoiceNumber}</TableCell>
                    <TableCell sx={{ fontSize: 12.5 }}>
                      {inv.customer?.firstName} {inv.customer?.lastName}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>
                      CI${(inv.pendingPayment?.amount || 0).toFixed(2)}
                    </TableCell>
                    <TableCell sx={{ fontSize: 12.5, textTransform: "capitalize" }}>
                      {(inv.pendingPayment?.method || "").replace("_", " ")}
                    </TableCell>
                    <TableCell sx={{ fontSize: 12, color: "text.secondary" }}>
                      {dayjs(inv.pendingPayment?.submittedAt).format("MMM D, h:mm A")}
                    </TableCell>
                    <TableCell>
                      <Button size="small" variant="outlined" onClick={() => handleViewProof(inv._id)}>
                        View
                      </Button>
                    </TableCell>
                    <TableCell align="right">
                      <Button
                        size="small"
                        color="error"
                        onClick={() => handleRejectReview(inv._id)}
                        disabled={reviewingId === inv._id}
                        sx={{ mr: 1 }}
                      >
                        Reject
                      </Button>
                      <Button
                        size="small"
                        variant="contained"
                        color="success"
                        onClick={() => handleApproveReview(inv._id)}
                        disabled={reviewingId === inv._id}
                      >
                        {reviewingId === inv._id ? <CircularProgress size={16} color="inherit" /> : "Approve"}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      <Card>
        <Tabs
          value={activeTab}
          onChange={(_, v) => setUrlState({ tab: slugifyTab(TABS[v].label), page: 0 })}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{
            maxWidth: "100%",
            px: 2,
            borderBottom: "1px solid",
            borderColor: "divider",
            "& .MuiTab-root": {
              fontSize: 13,
              textTransform: "none",
              fontWeight: 600,
              minHeight: 44,
            },
          }}
        >
          {TABS.map((t, i) => (
            <Tab key={i} label={t.label} />
          ))}
        </Tabs>

        {loading ? (
          <SkeletonLoader rows={6} />
        ) : invoices.length === 0 ? (
          <EmptyState
            icon="🧾"
            title="No invoices"
            description="Generate invoices for processed shipments."
            actionLabel="Generate Invoice"
            onAction={() => navigate("/admin/generate-invoice")}
          />
        ) : (
          <>
            {selectedIds.size > 0 && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  px: 2,
                  py: 1,
                  bgcolor: "primary.main",
                  color: "#fff",
                }}
              >
                <Typography variant="body2" fontWeight={600}>
                  {selectedIds.size} invoice{selectedIds.size !== 1 ? "s" : ""} selected
                </Typography>
                <Box sx={{ display: "flex", gap: 1 }}>
                  <Button size="small" sx={{ color: "#fff" }} onClick={() => setSelectedIds(new Set())}>
                    Clear
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    color="inherit"
                    sx={{ color: "primary.main", bgcolor: "#fff", "&:hover": { bgcolor: "#f0f0f0" } }}
                    startIcon={<DoneAll />}
                    onClick={() => setBulkConfirmOpen(true)}
                  >
                    Mark as Paid
                  </Button>
                </Box>
              </Box>
            )}
            <Box sx={{ overflowX: "auto" }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 42 }} padding="checkbox">
                      <Checkbox
                        size="small"
                        checked={allPageSelected}
                        indeterminate={somePageSelected && !allPageSelected}
                        onChange={toggleSelectAllOnPage}
                        disabled={selectablePageInvoices.length === 0}
                      />
                    </TableCell>
                    <TableCell>Invoice #</TableCell>
                    <TableCell>Customer</TableCell>
                    <TableCell>Shipment</TableCell>
                    <TableCell align="right">Amount</TableCell>
                    <TableCell align="right">Balance Due</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Due Date</TableCell>
                    <TableCell>Issued</TableCell>
                    <TableCell align="center" sx={{ width: 56 }} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {invoices.slice(page * 10, page * 10 + 10).map((inv) => {
                    const isExpanded = expandedId === inv._id;
                    const hasPayment = inv.balanceDue !== inv.totalAmount;
                    return (
                    <React.Fragment key={inv._id}>
                    <TableRow>
                      <TableCell padding="checkbox">
                        <Checkbox
                          size="small"
                          checked={selectedIds.has(inv._id)}
                          onChange={() => toggleSelected(inv._id)}
                          disabled={inv.balanceDue <= 0}
                        />
                      </TableCell>
                      <TableCell
                        sx={{
                          fontFamily: "monospace",
                          fontSize: 12,
                          color: "text.secondary",
                        }}
                      >
                        {inv.invoiceNumber}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>
                          {inv.customer?.firstName} {inv.customer?.lastName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {inv.customer?.email}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {inv.items?.length > 0 ? (
                          <Tooltip
                            title={inv.items.map((it) => it.trackingNumber || it.description).join(", ")}
                          >
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ fontFamily: "monospace", display: "block", maxWidth: 160 }}
                              noWrap
                            >
                              {inv.items
                                .slice(0, 3)
                                .map((it) => it.trackingNumber || "—")
                                .join(", ")}
                              {inv.items.length > 3 ? ` +${inv.items.length - 3} more` : ""}
                            </Typography>
                          </Tooltip>
                        ) : (
                          <>
                            <Typography variant="body2" noWrap maxWidth={160}>
                              {inv.shipment?.packageDescription || "—"}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ fontFamily: "monospace" }}
                            >
                              {inv.shipment?.trackingNumber}
                            </Typography>
                          </>
                        )}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600, whiteSpace: "nowrap" }}>
                        <Typography variant="body2" fontWeight={600}>
                          CI${inv.totalAmount?.toFixed(2)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block">
                          ≈ ${ciToUsd(inv.totalAmount, usdToCiRate).toFixed(2)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                        <Typography
                          variant="body2"
                          fontWeight={700}
                          color={inv.balanceDue > 0 ? "error.main" : "success.main"}
                        >
                          CI${inv.balanceDue?.toFixed(2)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block">
                          ≈ ${ciToUsd(inv.balanceDue, usdToCiRate).toFixed(2)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <StatusChip status={inv.status} domain="invoice" />
                      </TableCell>
                      <TableCell
                        sx={{
                          fontSize: 12,
                          color:
                            dayjs(inv.dueDate).isBefore(dayjs()) &&
                            inv.status !== paidKey
                              ? "error.main"
                              : "text.secondary",
                        }}
                      >
                        {dayjs(inv.dueDate).format("MMM D, YYYY")}
                      </TableCell>
                      <TableCell sx={{ fontSize: 12, color: "text.secondary" }}>
                        {dayjs(inv.issuedAt).format("MMM D")}
                      </TableCell>
                      <TableCell align="center">
                        <IconButton
                          size="small"
                          onClick={() => setExpandedId(isExpanded ? null : inv._id)}
                        >
                          {isExpanded ? (
                            <KeyboardArrowUp fontSize="small" />
                          ) : (
                            <KeyboardArrowDown fontSize="small" />
                          )}
                        </IconButton>
                      </TableCell>
                    </TableRow>
                    {isExpanded && (
                      <TableRow>
                        <TableCell colSpan={10} sx={{ py: 1.5 }}>
                          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<Edit />}
                              onClick={() => setEditInvoice(inv)}
                            >
                              Edit
                            </Button>
                            {inv.balanceDue > 0 && (
                              <Button
                                size="small"
                                variant="outlined"
                                color="success"
                                startIcon={<DoneAll />}
                                onClick={() => setMarkPaidInvoice(inv)}
                              >
                                Mark as Paid
                              </Button>
                            )}
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<Download />}
                              onClick={() => navigate(`/admin/invoices/${inv.invoiceNumber}/print`)}
                            >
                              PDF
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={
                                resendingId === inv._id ? (
                                  <CircularProgress size={14} />
                                ) : (
                                  <Send />
                                )
                              }
                              disabled={resendingId === inv._id}
                              onClick={() => handleResend(inv)}
                            >
                              Resend
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              color="error"
                              startIcon={<Delete />}
                              disabled={hasPayment}
                              onClick={() => setInvoiceToDelete(inv)}
                            >
                              Delete
                            </Button>
                          </Box>
                        </TableCell>
                      </TableRow>
                    )}
                    </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>
            <TablePagination
              component="div"
              count={invoices.length}
              page={page}
              onPageChange={(_, p) => setUrlState({ page: p })}
              rowsPerPage={10}
              rowsPerPageOptions={[10]}
              sx={{ borderTop: "1px solid", borderColor: "divider" }}
            />
          </>
        )}
      </Card>

      <InvoiceDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onSubmit={handleCreate}
        loading={creating}
      />

      <EditInvoiceDialog
        open={!!editInvoice}
        onClose={() => setEditInvoice(null)}
        onSubmit={handleEditSave}
        loading={editing}
        invoice={editInvoice}
      />

      <MarkInvoicePaidDialog
        open={!!markPaidInvoice}
        invoice={markPaidInvoice}
        onClose={() => setMarkPaidInvoice(null)}
        onPaid={load}
      />

      <Dialog open={!!invoiceToDelete} onClose={() => setInvoiceToDelete(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography fontWeight={700}>Delete Invoice</Typography>
          <IconButton size="small" onClick={() => setInvoiceToDelete(null)}>
            <Close fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2">
            Delete invoice <strong>{invoiceToDelete?.invoiceNumber}</strong> for{" "}
            <strong>
              {invoiceToDelete?.customer?.firstName} {invoiceToDelete?.customer?.lastName}
            </strong>
            ? Its items will become un-invoiced again so they can be billed on a new invoice. This cannot be undone.
          </Typography>
          {invoiceToDelete?.balanceDue !== invoiceToDelete?.totalAmount && (
            <Alert severity="warning" sx={{ mt: 2, borderRadius: 2 }}>
              This invoice has payment(s) recorded against it — deleting it also deletes those payment records.
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setInvoiceToDelete(null)} variant="outlined">
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            variant="contained"
            color="error"
            disabled={deleting}
          >
            {deleting ? <CircularProgress size={18} color="inherit" /> : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={bulkConfirmOpen} onClose={() => setBulkConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography fontWeight={700}>Mark {selectedIds.size} Invoice{selectedIds.size !== 1 ? "s" : ""} as Paid</Typography>
          <IconButton size="small" onClick={() => setBulkConfirmOpen(false)}>
            <Close fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Alert severity="info" sx={{ borderRadius: 2 }}>
            This records full payment on each selected invoice directly — no proof/screenshot needed. Use this for
            payments confirmed outside the app (cash, bank transfer, etc).
          </Alert>
          <TextField select fullWidth label="Payment Method" value={bulkMethod} onChange={(e) => setBulkMethod(e.target.value)}>
            <MenuItem value="cash">Cash</MenuItem>
            <MenuItem value="bank_transfer">EFT</MenuItem>
            <MenuItem value="card">Card</MenuItem>
          </TextField>
          <TextField
            fullWidth
            label="Notes (optional)"
            placeholder="e.g. Confirmed via bank statement Sept 15"
            value={bulkNotes}
            onChange={(e) => setBulkNotes(e.target.value)}
            multiline
            rows={2}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setBulkConfirmOpen(false)} variant="outlined">
            Cancel
          </Button>
          <Button onClick={handleBulkMarkPaid} variant="contained" disabled={bulkPaying}>
            {bulkPaying ? <CircularProgress size={18} color="inherit" /> : "Mark as Paid"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminInvoices;
